import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GroupSidebar } from "./components/groupSidebar.component";
import { GroupMembersTab } from "./components/groupMembersTab.component";
import { GroupExamsTab } from "./components/groupExamsTab.component";
import { KorrekturStepAssignmentProvider } from "./contexts/korrekturStepAssignment.context";

import type { GroupSummary } from "./types.js";
import type {
	OrgaAdminGruppeWithAssignmentStatistics,
	OrgaAdminGruppeDTO,
} from "@/lib/orgaAdmin/orgaAdmin.types.js";
import OrgaAdminGruppeService from "@/lib/orgaAdmin/orgaAdminGruppe.service.js";
import { useAuth } from "@/lib/contexts/auth.context.js";
import { Button } from "@/components/ui/button.js";
import { ArrowLeft } from "lucide-react";

export type GruppenOverviewTabs = "users" | "exams" | (string & {});

/**
 * Overview of all groups in an organization
 */
export default function GruppenOverview() {
	const [backendGroups, setBackendGroups] = useState<OrgaAdminGruppeDTO[]>([]);
	const [groupStatistics, setGroupStatistics] = useState<
		Map<string, OrgaAdminGruppeWithAssignmentStatistics>
	>(new Map());
	const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
	const [search, setSearch] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [activeTab, setActiveTab] = useState<GruppenOverviewTabs>("exams");
	const { currentOrganization } = useAuth();
	const hasAutoOpenedFirstGroupRef = useRef(false);
	const lastOrganizationIdRef = useRef<string | undefined>(undefined);

	// Fetch statistics for a specific group
	const fetchGroupStatistics = useCallback(
		async (groupId: string) => {
			if (!currentOrganization) return;

			try {
				const statistics =
					await OrgaAdminGruppeService.getInstance().getStatisticsForGruppe(
						currentOrganization.organizationId,
						groupId,
					);
				setGroupStatistics((prev) => new Map(prev.set(groupId, statistics)));
			} catch (error) {
				console.error(`Failed to load statistics for group ${groupId}:`, error);
			}
		},
		[currentOrganization],
	);

	// Fetch statistics for all groups
	const fetchAllGroupStatistics = useCallback(
		async (groups: OrgaAdminGruppeDTO[]) => {
			if (!currentOrganization) return;

			const statisticsPromises = groups.map((group) =>
				fetchGroupStatistics(group.id),
			);

			await Promise.allSettled(statisticsPromises);
		},
		[currentOrganization, fetchGroupStatistics],
	);

	useEffect(() => {
		if (!currentOrganization) return;

		// Reset auto-open when switching organizations
		if (lastOrganizationIdRef.current !== currentOrganization.organizationId) {
			lastOrganizationIdRef.current = currentOrganization.organizationId;
			hasAutoOpenedFirstGroupRef.current = false;
			setSelectedGroupId(null);
		}

		setIsLoading(true);
		// load basic groups from backend (without statistics to avoid studentKlausuren)
		OrgaAdminGruppeService.getInstance()
			.getGruppenInOrganization(currentOrganization.organizationId, false) // fetch groups WITHOUT statistics
			.then(async (groups) => {
				console.log("Backend groups:", groups);
				const basicGroups = groups as OrgaAdminGruppeDTO[];
				setBackendGroups(basicGroups);

				// On initial load, open the first group by default
				if (!hasAutoOpenedFirstGroupRef.current && basicGroups.length > 0) {
					setSelectedGroupId(basicGroups[0].id);
					hasAutoOpenedFirstGroupRef.current = true;
				}

				// Fetch statistics for all groups separately
				await fetchAllGroupStatistics(basicGroups);
			})
			.catch((error) => {
				console.error("Failed to load groups:", error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [currentOrganization, fetchAllGroupStatistics]);

	// Convert backend group to group summary for display
	const mapBackendGroupToSummary = useCallback(
		(backendGroup: OrgaAdminGruppeDTO): GroupSummary => {
			const statistics = groupStatistics.get(backendGroup.id);
			let completionPercentage = 0;
			let correctionPercentage = 0;

			if (statistics) {
				completionPercentage = Math.round(
					statistics.korrekturStepsWithZuweisungPercentage,
				);
				correctionPercentage = Math.round(
					statistics.korrekturStepsCompletedPercentage,
				);
			}

			return {
				id: backendGroup.id,
				name: backendGroup.title,
				description: `${backendGroup.klausuren?.length || 0} Klausuren`,
				userCount: backendGroup.users?.length || 0,
				examCount: Array.isArray(backendGroup.klausuren)
					? backendGroup.klausuren.length
					: 0,
				assignmentCount: 0,
				completionPercentage,
				correctionCount: 0,
				correctionPercentage,
			};
		},
		[groupStatistics],
	);

	const groupSummaries = useMemo(() => {
		return backendGroups.map(mapBackendGroupToSummary);
	}, [backendGroups, mapBackendGroupToSummary]);

	const filteredGroups = useMemo(() => {
		return groupSummaries.filter((group) => {
			// Text search filter
			const matchesSearch =
				group.name.toLowerCase().includes(search.toLowerCase()) ||
				group.description?.toLowerCase().includes(search.toLowerCase());

			return matchesSearch;
		});
	}, [groupSummaries, search]);

	const selectedBackendGroup = backendGroups.find(
		(g) => g.id === selectedGroupId,
	);

	const handleViewGroup = (groupId: string) => {
		setSelectedGroupId(groupId);
	};

	const handleTabChange = useCallback((value: GruppenOverviewTabs) => {
		setActiveTab(value);
	}, []);

	const handleNavigateToAssignment = useCallback(() => {
		setActiveTab("exams");
	}, []);

	// Refresh data after assignment operations
	const handleOperationComplete = useCallback(() => {
		// Refresh the backend group data to get updated information
		if (selectedGroupId && currentOrganization) {
			const fetchGroupData = async () => {
				try {
					const orgaAdminGruppeService = OrgaAdminGruppeService.getInstance();
					const groups = await orgaAdminGruppeService.getGruppenInOrganization(
						currentOrganization.organizationId,
					);
					setBackendGroups(groups as OrgaAdminGruppeDTO[]);
				} catch (err) {
					console.error("Error refreshing group data:", err);
				}
			};
			fetchGroupData();
		}
	}, [selectedGroupId, currentOrganization]);

	return (
		<div className="flex h-main-content">
			{/* Sidebar */}
			<GroupSidebar
				groupSummaries={filteredGroups}
				selectedGroupId={selectedGroupId}
				isLoading={isLoading}
				onSearch={setSearch}
				onSelectGroup={handleViewGroup}
			/>

			{/* Main Content */}
			{selectedGroupId && (
				<div className="flex-1 flex flex-col overflow-y-auto h-main-content">
					<div className="flex-1 overflow-auto py-6 pr-6 pl-2">
						<KorrekturStepAssignmentProvider
							key={selectedGroupId}
							groupId={selectedGroupId}
							selectedBackendGroup={selectedBackendGroup}
							onOperationComplete={handleOperationComplete}
						>
							<Tabs
								value={activeTab}
								onValueChange={handleTabChange}
								className="h-full flex flex-col"
							>
								<div className="flex items-center gap-2">
									<Button
										variant="ghost"
										onClick={() => setSelectedGroupId(null)}
									>
										<ArrowLeft className="w-4 h-4" />
									</Button>
									<TabsList className="grid w-full grid-cols-2 bg-primary/10">
										<TabsTrigger value="users">
											Personen ({selectedBackendGroup?.users?.length || 0})
										</TabsTrigger>
										<TabsTrigger value="exams">
											Klausuren ({selectedBackendGroup?.klausuren?.length || 0})
										</TabsTrigger>
									</TabsList>
								</div>
								<TabsContent value="users" className="mt-4">
									<GroupMembersTab
										selectedBackendGroup={selectedBackendGroup}
										onNavigateToAssignment={handleNavigateToAssignment}
									/>
								</TabsContent>

								<TabsContent value="exams" className="mt-4">
									<GroupExamsTab />
								</TabsContent>
							</Tabs>
						</KorrekturStepAssignmentProvider>
					</div>
				</div>
			)}
		</div>
	);
}
